import { useMemo } from "react";
import DSBox from "views/system-design/DSBox";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSTextField from "views/system-design/DSTextField";
import MultiSelectFilter, { FilterField } from "components/MultiSelectFilter";
import useUsers from "components/hooks/useUsers";
import {
  TicketFilters,
  TICKET_STATUSES,
  TICKET_TYPES,
  TICKET_PRIORITIES,
} from "../types";
import { TicketFacets } from "api/tickets";
import { buildUserLookup, sanitizeVersion } from "../utils";

function isRealPersonName(name: string): boolean {
  return name.length > 0 && !/\d/.test(name);
}

interface TicketFiltersPanelProps {
  filters: TicketFilters;
  onFiltersChange: (filters: TicketFilters) => void;
  onClearFilters: () => void;
  facets: TicketFacets;
}

export default function TicketFiltersPanel({
  filters,
  onFiltersChange,
  onClearFilters,
  facets,
}: TicketFiltersPanelProps) {
  const { data: allUsers } = useUsers();
  const usersByIdOrName = useMemo(
    () => buildUserLookup(allUsers ?? []),
    [allUsers],
  );

  // Options come from the facet aggregation, so they list every label and
  // reporter on the board rather than the ones in the loaded pages.
  const allLabels = useMemo(
    () =>
      facets.labelStats.map(({ label }) => ({
        value: label,
        label,
      })),
    [facets.labelStats],
  );

  const reporterNamesById = useMemo(() => {
    const namesById = new Map<string, string>();
    for (const { value } of facets.reporters) {
      const reporter = usersByIdOrName.get(value.trim());
      if (!reporter) continue;
      const reporterName = reporter.name.trim();
      if (!isRealPersonName(reporterName)) continue;
      namesById.set(reporter.id, reporterName);
    }
    return namesById;
  }, [facets.reporters, usersByIdOrName]);

  // A reporter already selected but no longer in the list — a removed user —
  // stays selectable, otherwise the filter hides every ticket with no way to
  // switch it off.
  const allReporters = useMemo(() => {
    const knownReporters = Array.from(reporterNamesById, ([value, label]) => ({
      value,
      label,
    })).sort((first, second) => first.label.localeCompare(second.label));
    const orphanReporters = filters.reporters
      .filter((reporterId) => !reporterNamesById.has(reporterId))
      .map((reporterId) => ({
        value: reporterId,
        label: usersByIdOrName.get(reporterId)?.name.trim() ?? reporterId,
      }));
    return [...knownReporters, ...orphanReporters];
  }, [reporterNamesById, filters.reporters, usersByIdOrName]);

  return (
    <DSBox
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        mt: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: "white.5",
        flexWrap: "wrap",
      }}
    >
      <MultiSelectFilter
        label="Status"
        selected={filters.statuses}
        options={TICKET_STATUSES}
        allLabel="All Statuses"
        onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
      />

      <MultiSelectFilter
        label="Type"
        selected={filters.types}
        options={TICKET_TYPES}
        allLabel="All Types"
        onChange={(types) => onFiltersChange({ ...filters, types })}
      />

      <MultiSelectFilter
        label="Priority"
        selected={filters.priorities}
        options={TICKET_PRIORITIES}
        allLabel="All Priorities"
        onChange={(priorities) => onFiltersChange({ ...filters, priorities })}
      />

      <MultiSelectFilter
        label="Reporter"
        selected={filters.reporters}
        options={allReporters}
        allLabel="All Reporters"
        onChange={(reporters) => onFiltersChange({ ...filters, reporters })}
      />

      <MultiSelectFilter
        label="Label"
        selected={filters.labels}
        options={allLabels}
        allLabel="All Labels"
        onChange={(labels) => onFiltersChange({ ...filters, labels })}
      />

      <FilterField label="Version">
        <DSTextField
          size="small"
          placeholder="e.g. 7.2.0"
          value={filters.targetVersion}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              targetVersion: sanitizeVersion(event.target.value),
            })
          }
          sx={{ width: 120 }}
        />
      </FilterField>

      <FilterField label="Updated From">
        <DSTextField
          type="date"
          size="small"
          value={filters.updatedFrom}
          onChange={(event) =>
            onFiltersChange({ ...filters, updatedFrom: event.target.value })
          }
          inputProps={{ max: filters.updatedTo || undefined }}
          sx={{ width: 150 }}
        />
      </FilterField>

      <FilterField label="Updated To">
        <DSTextField
          type="date"
          size="small"
          value={filters.updatedTo}
          onChange={(event) =>
            onFiltersChange({ ...filters, updatedTo: event.target.value })
          }
          inputProps={{ min: filters.updatedFrom || undefined }}
          sx={{ width: 150 }}
        />
      </FilterField>

      <DSSecondaryButton size="medium" onClick={onClearFilters}>
        Clear
      </DSSecondaryButton>
    </DSBox>
  );
}
